/**
 * Challenge reward mapping helpers (single source of truth).
 *
 * The challenge editor authors rewards as `{ amount, type }` rows where
 * `type` is one of the UI `RewardType` pills. The BE create/update endpoints
 * expect `{ amount, currency, tier }` entries instead, where:
 *   - `currency` is the BE money type the reward pays out in (`play` /
 *     `value`) — derived from the UI `RewardType` via
 *     `REWARD_TYPE_TO_CHALLENGE_CURRENCY`;
 *   - `tier` is the audience the reward applies to (`free` / `premium`) —
 *     derived from the challenge's `path_availability` (the Free / Premium
 *     tab the challenge was authored under).
 *
 * Both create (`useUpdateChallengeReward` after POST) and update
 * (`useUpdateChallengeReward`) mappers build their `rewards` array through
 * `mapChallengeRewardsToRequest` so the wire shape stays consistent, and the Season Details reverse mapper
 * reads `CHALLENGE_CURRENCY_TO_REWARD_TYPE` so the round-trip
 * (UI → BE → UI on edit) is provably consistent.
 */

import type {
  ChallengeDraft,
  RewardDraft,
  RewardType,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import { AppStrings } from "../../utils/constants/AppStrings";
import type {
  ChallengeApiRecord,
  ChallengePathAvailability,
  ChallengeRewardCurrency,
  ChallengeRewardTier,
  CreateChallengeRewardRequest,
} from "../../types/gamePass";

/** UI reward-type pill → BE reward `currency`. Cash-denominated rewards
 *  (Free Cash Pick, Profit Boost, Rebet Cash, USD, Nira) pay out of the
 *  redeemable `value` balance; play-money rewards (Rebet Coins, XP) pay out of
 *  the `play` balance. Kept as an exhaustive `Record` so adding a new
 *  `RewardType` is a compile error here until its currency is decided. */
export const REWARD_TYPE_TO_CHALLENGE_CURRENCY: Record<
  RewardType,
  ChallengeRewardCurrency
> = {
  FREE_CASH_PICK: "value",
  PROFIT_BOOST: "value",
  REBET_CASH: "value",
  USD: "value",
  NIRA: "value",
  REBET_COINS: "play",
  XP: "play",
};

/** BE reward `currency` → UI reward-type pill, used by the Season Details
 *  reverse mapper when re-hydrating a saved challenge into the editor. The
 *  forward map above is many-to-one (five types map to `value`, two to
 *  `play`), so the reverse picks the canonical pill for each currency. */
export const CHALLENGE_CURRENCY_TO_REWARD_TYPE: Record<
  ChallengeRewardCurrency,
  RewardType
> = {
  value: "REBET_CASH",
  play: "REBET_COINS",
};

/** Challenge `path_availability` → the reward tiers the rewards apply to. A
 *  `BOTH` challenge emits each reward once per tier so free and premium
 *  players each receive it. */
const PATH_AVAILABILITY_TO_TIERS: Record<
  ChallengePathAvailability,
  ReadonlyArray<ChallengeRewardTier>
> = {
  FREE: ["free"],
  PREMIUM: ["premium"],
  BOTH: ["free", "premium"],
};

/** Parse a reward-amount input into the positive number the BE expects, or
 *  `null` when the row is blank / non-numeric / non-positive (those rows are
 *  dropped). Amounts may be fractional (e.g. a `10.0` value payout), so the
 *  value is NOT floored. */
const parseRewardAmount = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Convert the challenge editor's reward rows into the BE `rewards` payload.
 *
 * Only rows that carry BOTH a reward type AND a valid positive amount are
 * emitted — incomplete / in-flight rows (no type picked, blank or `0` amount,
 * non-numeric text) are skipped so the wire never carries a half-authored
 * reward. Each surviving row is emitted once per tier implied by the
 * challenge's `pathAvailability` (`BOTH` → one entry for `free` and one for
 * `premium`).
 */
const VALUE_REWARD_LABEL = AppStrings.REWARD_TYPE_REBET_CASH;
const PLAY_REWARD_LABEL = AppStrings.REWARD_TYPE_REBET_COINS;

/** Formats a persisted challenge reward for read-only admin surfaces. The BE
 *  wire only carries `amount` + `currency`, so rewards use their configured
 *  product-currency labels rather than the account's fiat symbol. */
export const formatChallengeApiRewardLabel = (
  reward: CreateChallengeRewardRequest,
): string => {
  const amount = reward.amount;

  if (reward.currency === "value") {
    return `${amount} ${VALUE_REWARD_LABEL}`;
  }

  if (reward.currency === "play") {
    return `${amount} ${PLAY_REWARD_LABEL}`;
  }

  return String(amount);
};

/** Returns the reward labels that apply to the challenge's canonical tier,
 *  collapsing duplicate free/premium copies authored on `BOTH` challenges. */
export const getChallengeRewardLabels = (
  challenge: Pick<ChallengeApiRecord, "rewards" | "path_availability">,
): string[] => {
  if (!Array.isArray(challenge.rewards)) {
    return [];
  }

  const canonicalTier: ChallengeRewardTier =
    challenge.path_availability === "PREMIUM" ? "premium" : "free";

  return challenge.rewards
    .filter((reward) => reward.tier === canonicalTier)
    .map(formatChallengeApiRewardLabel);
};

/** Coerce a bonus reward's param values (kept as strings in the editor) to the
 *  numeric / string shape the BE expects. Blank values are dropped so an
 *  untouched optional param is omitted rather than sent as `""`; numeric-looking
 *  values ship as numbers, everything else stays a string. */
export const coerceRewardParams = (
  params: RewardDraft["params"],
): Record<string, string | number> | undefined => {
  if (!params) return undefined;
  const coerced: Record<string, string | number> = {};
  for (const [name, rawValue] of Object.entries(params)) {
    const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;
    if (value === "" || value === undefined || value === null) continue;
    const asNumber = Number(value);
    coerced[name] = Number.isFinite(asNumber) ? asNumber : String(value);
  }
  return Object.keys(coerced).length > 0 ? coerced : undefined;
};

export const mapChallengeRewardsToRequest = (
  rewards: ChallengeDraft["rewards"],
  pathAvailability: ChallengePathAvailability,
): CreateChallengeRewardRequest[] => {
  const tiers = PATH_AVAILABILITY_TO_TIERS[pathAvailability] ?? ["free"];
  const mapped: CreateChallengeRewardRequest[] = [];
  for (const reward of rewards) {
    if (!reward.type) continue;
    const amount = parseRewardAmount(reward.amount);
    if (amount === null) continue;

    // Bonus rewards carry their own grant identifier + tunable params; a
    // currency reward maps its UI type onto the play/value money type.
    if (reward.bonusRewardType) {
      const params = coerceRewardParams(reward.params);
      for (const tier of tiers) {
        mapped.push({
          amount,
          currency: reward.bonusCurrency ?? reward.bonusRewardType,
          tier,
          reward_type: reward.bonusRewardType,
          ...(params ? { params } : {}),
        });
      }
      continue;
    }

    const currency = REWARD_TYPE_TO_CHALLENGE_CURRENCY[reward.type as RewardType];
    if (!currency) continue;
    for (const tier of tiers) {
      mapped.push({ amount, currency, tier });
    }
  }
  return mapped;
};
